import { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles,
  X,
  Send,
  Loader2,
  Trash2,
  Database,
  HelpCircle,
} from "lucide-react";
import {
  CopilotoService,
  type CopilotoMessage,
} from "@/services/copilotoService";
import "./copiloto.css";

const STORAGE_KEY = "copiloto_goon_history";
const MAX_STORED = 40;

interface ChatMessage extends CopilotoMessage {
  toolsUsed?: string[];
  error?: boolean;
}

const SUGGESTIONS = [
  "Quais desks estão atrasadas?",
  "Quais tarefas minhas estão pendentes?",
  "Resumo das prospecções inbound",
  "Como estão minhas metas deste mês?",
];

const TOOL_LABELS: Record<string, string> = {
  buscar_desks: "Desks",
  buscar_prospeccoes: "Prospecções",
  buscar_tarefas: "Tarefas",
  buscar_metas: "Metas",
  buscar_clientes: "Clientes",
};

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CopilotoGoOn() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(loadHistory);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(messages.slice(-MAX_STORED)),
      );
    } catch {
      /* storage cheio/indisponível — ignorar */
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen, scrollToBottom]);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "44px";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  useEffect(() => {
    adjustHeight();
  }, [input]);

  const handleSend = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || isSending) return;

    const userMsg: ChatMessage = { role: "user", content: text };
    const history = messages.map(({ role, content }) => ({ role, content }));

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsSending(true);
    if (textareaRef.current) textareaRef.current.style.height = "44px";

    const res = await CopilotoService.sendMessage(text, history);

    if (res.success) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: res.reply,
          toolsUsed: res.toolsUsed,
        },
      ]);
    } else {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            res.message || "Não consegui responder agora. Tente novamente.",
          error: true,
        },
      ]);
    }
    setIsSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      {/* Botão flutuante (FAB) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            key="copiloto-fab"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-24 right-6 z-[9] flex items-center gap-2 rounded-full bg-gradient-to-br from-azul-400 to-azul-600 px-4 py-3 text-white shadow-lg transition-all hover:shadow-xl hover:scale-105"
            title="Abrir Copiloto Ad Desk"
          >
            <Sparkles size={20} className="text-yellow-300" />
            <span className="hidden text-sm font-semibold sm:inline">
              Copiloto Ad Desk
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Painel do chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="copiloto-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed bottom-6 right-6 z-[9] flex h-[36rem] max-h-[85vh] w-[92vw] max-w-[26rem] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-gradient-to-br from-azul-400 to-azul-600 p-3.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                  <Sparkles size={18} className="text-yellow-300" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white">
                    Copiloto Ad Desk
                  </h3>
                  <p className="text-xs text-white/80">
                    Seu assistente de dados
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="relative">
                  <button
                    onMouseEnter={() => setShowHelp(true)}
                    onMouseLeave={() => setShowHelp(false)}
                    className="rounded p-1.5 transition-colors hover:bg-white/20"
                    title="Ajuda e exemplos"
                  >
                    <HelpCircle className="text-white" size={16} />
                  </button>
                  {showHelp && (
                    <div className="absolute right-0 top-full mt-2 w-72 rounded-xl bg-white p-4 shadow-2xl z-10">
                      <h4 className="mb-2 text-sm font-semibold text-cinza-700">
                        💡 Como usar o Copiloto
                      </h4>
                      <div className="space-y-3 text-xs text-cinza-600">
                        <div>
                          <p className="mb-1 font-medium text-azul-600">
                            📊 Consultas de dados:
                          </p>
                          <ul className="ml-4 space-y-0.5 list-disc">
                            <li>"Quais desks estão atrasadas?"</li>
                            <li>"Mostre minhas tarefas pendentes"</li>
                            <li>"Resumo das prospecções ativas"</li>
                          </ul>
                        </div>
                        <div>
                          <p className="mb-1 font-medium text-azul-600">
                            🎯 Metas e análises:
                          </p>
                          <ul className="ml-4 space-y-0.5 list-disc">
                            <li>"Como está minha meta deste mês?"</li>
                            <li>"Clientes com mais de 5 desks"</li>
                          </ul>
                        </div>
                        <div>
                          <p className="mb-1 font-medium text-azul-600">
                            ⚡ Dicas:
                          </p>
                          <ul className="ml-4 space-y-0.5 list-disc">
                            <li>Use filtros: "últimos 7 dias", "este mês"</li>
                            <li>Peça comparações e análises</li>
                            <li>Solicite tabelas ou resumos</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {messages.length > 0 && (
                  <button
                    onClick={handleClear}
                    className="rounded p-1.5 transition-colors hover:bg-white/20"
                    title="Limpar conversa"
                  >
                    <Trash2 className="text-white" size={16} />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded p-1.5 transition-colors hover:bg-white/20"
                  title="Fechar"
                >
                  <X className="text-white" size={18} />
                </button>
              </div>
            </div>

            {/* Mensagens */}
            <div className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4">
              {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-azul-400 to-azul-600">
                    <Sparkles size={26} className="text-yellow-300" />
                  </div>
                  <p className="mb-1 text-sm font-semibold text-cinza-700">
                    Olá! Sou o Copiloto Ad Desk 👋
                  </p>
                  <p className="mb-4 max-w-[16rem] text-xs text-cinza-400">
                    Posso analisar suas desks, prospecções, tarefas, metas e
                    clientes. Pergunte algo:
                  </p>
                  <div className="flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSend(s)}
                        className="rounded-full border border-azul-400/30 bg-white px-3 py-1.5 text-xs text-azul-600 transition-colors hover:bg-azul-400 hover:text-white"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <MessageBubble key={idx} message={msg} />
                ))
              )}

              {isSending && (
                <div className="flex items-center gap-2 text-cinza-400">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-azul-400 to-azul-600">
                    <Sparkles size={14} className="text-yellow-300" />
                  </div>
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-white px-3 py-2.5 shadow-sm">
                    <Loader2 className="animate-spin text-azul-400" size={14} />
                    <span className="text-xs">Analisando seus dados…</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-gray-100 bg-white p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Pergunte sobre seus dados…"
                  rows={1}
                  disabled={isSending}
                  className="flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-cinza-700 outline-none transition-colors focus:border-azul-400 focus:bg-white disabled:opacity-60"
                  style={{ height: "44px", maxHeight: "120px" }}
                />
                <button
                  onClick={() => handleSend()}
                  disabled={isSending || !input.trim()}
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-azul-400 to-azul-600 text-white transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                  title="Enviar"
                >
                  {isSending ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-gradient-to-br from-azul-400 to-azul-600 px-3.5 py-2.5 text-sm text-white shadow-sm">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-azul-400 to-azul-600">
        <Sparkles size={14} className="text-yellow-300" />
      </div>
      <div className="max-w-[85%] space-y-1.5">
        <div
          className={`rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm shadow-sm ${
            message.error ? "bg-red-50 text-red-600" : "bg-white text-cinza-700"
          }`}
        >
          <div className="copiloto-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-azul-600 hover:text-azul-700 underline font-medium"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
        {message.toolsUsed && message.toolsUsed.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 pl-1">
            <Database size={11} className="text-cinza-400" />
            {Array.from(new Set(message.toolsUsed)).map((tool) => (
              <span
                key={tool}
                className="rounded-full bg-azul-400/10 px-1.5 py-0.5 text-[10px] font-medium text-azul-600"
              >
                {TOOL_LABELS[tool] || tool}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
